//imports
import React, { useEffect, Fragment, useCallback, useState, useRef } from 'react';
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from 'react-redux';
import { useRealtimeTags } from 'Core/hooks';

import { LogManagerConsole } from '../../../../../../core/logManager'

import { NotificationManager } from 'Components/Notifications';
import { HasPermission } from "Core/security"
import { GetFormatForMoment } from 'Core/data/Helpers';
import { TagsGroup } from 'Components/Tag';
import { Timezone } from 'Components/uiControls'
import moment from 'moment';

import {
    TableView,
    TableButtons,
    Button,
    IconButton,
    SearchInput,
    MenuComponent,
    Tooltip,
    Container,
    Pill
} from '@gpstrackit/gtc-react-controls';

import { Grid, Grow, Paper, Typography } from "@mui/material";
import AdvancedFilters from './components/advancedFilters';
import DesignForm from '../../../../modals/design';
import DeleteForms from '../../../../modals/delete';
import DuplicateForm from '../../../../modals/duplicate';
import RenameForm from '../../../../modals/rename';

import {
    sendGenericExport,
    clearRedux
} from "Redux/actions";

import {
    reset,
    searchDynamicForms,
    openAdvancedFilters,
    closeAdvancedFilters
} from './redux/slice';

import {
    reset as resetDynamicFormAnswers,
    searchDynamicFormAnswers,
    getDynamicForms
} from '../answers/redux/slice';

import {
    openDesignModal
} from '../../../../modals/design/redux/slice';

import {
    openDeleteModal
} from '../../../../modals/delete/redux/slice';

import {
    openDuplicateModal
} from '../../../../modals/duplicate/redux/slice';

import {
    openRenameModal
} from '../../../../modals/rename/redux/slice';

//Hook component
const DynamicFormsList = (props) => {
    const { messages, locale } = useIntl();
    const dispatch = useDispatch();

    const tableRef = useRef();
    //Redux
    const exportResult = useSelector(state => state.commomRedux.exportResult);
    const exportName = useSelector(state => state.commomRedux.exportName);

    const filters = useSelector(state => state.dynamicFormsRedux.filters);
    const openAdvancedFiltersModal = useSelector(state => state.dynamicFormsRedux.openAdvancedFilters);
    const openDesignFormModal = useSelector(state => state.dynamicFormsDesignRedux.open);
    const loading = useSelector(state => state.dynamicFormsRedux.loading);
    const data = useSelector(state => state.dynamicFormsRedux.data);
    const error = useSelector(state => state.dynamicFormsRedux.error);

    const openDeleteFormsModal = useSelector(state => state.dynamicFormsDeleteRedux.open);
    const openDuplicateFormModal = useSelector(state => state.dynamicFormsDuplicateRedux.open);
    const openRenameFormModal = useSelector(state => state.dynamicFormsRenameRedux.open);

    const answersFilters = useSelector(state => state.dynamicFormAnswersRedux.filters);

    const user = useSelector((state) => state.securityRedux.user);

    const tags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });
    const loadingTags = useSelector(state => state.catalogsRedux.loadingTags);

    const { dateformat, timeZone } = user;
    const format = GetFormatForMoment(dateformat);

    const options = [
        messages['dynamicFormsMain_edit'],
        messages['dynamicFormsMain_rename'],
        messages['dynamicFormsMain_viewAnswers'],
        messages['dynamicFormsMain_copy'],
        messages['dynamicFormsMain_delete'],
    ];

    const [columns, setColumns] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        LogManagerConsole("DynamicFroms_Main", "load");
        dispatch(searchDynamicForms(filters));
        return () => dispatch(reset())
    }, []);

    useEffect(() => {
        setColumns([
            {
                key: 'name',
                title: messages['dynamicFormsMain_name'],
                show: true,
                export: true,
                render: (row) => {
                    return (
                        <Grid
                            container
                            alignItems="center"
                            justifyContent="space-between"
                            className="context-menu-hover-container"
                            wrap="nowrap"
                        >
                            <Grid item xs zeroMinWidth style={{ width: 150 }}>
                                <Tooltip placement='top' title={row?.name}>
                                    <Typography noWrap>{row?.name}</Typography>
                                </Tooltip>
                            </Grid>
                            <Grid item justifyContent="flex-end">
                                <MenuComponent
                                    disabled={loading}
                                    options={options}
                                    placement="right"
                                    icon={'more_vert'}
                                    className="context-menu-hover"
                                    onClick={(type) => {
                                        if (type === messages['dynamicFormsMain_edit']) {
                                            dispatch(openDesignModal(row?.id));
                                        } else if (type === messages['dynamicFormsMain_delete']) {
                                            dispatch(openDeleteModal([row]));
                                        } else if (type === messages['dynamicFormsMain_copy']) {
                                            dispatch(openDuplicateModal(row));
                                        } else if (type === messages['dynamicFormsMain_rename']) {
                                            dispatch(openRenameModal(row));
                                        } else if (type === messages['dynamicFormsMain_viewAnswers']) {
                                            let newAnswersFilters = {
                                                ...answersFilters,
                                                openSearch: "",
                                                filters: [{ filterBy: "formId", filterValue: row?.id }],
                                                nextToken: null,
                                                resetSearch: true,
                                                dateRange: {
                                                    start: moment().subtract(7, 'days').startOf('day').toDate(),
                                                    end: moment.max(moment()).endOf('day').toDate()
                                                },
                                                avoidFirstLoad: true
                                            };

                                            dispatch(searchDynamicFormAnswers(newAnswersFilters));
                                            dispatch(resetDynamicFormAnswers({ filters: newAnswersFilters, items: [], nextToken: null }));

                                            if (props?.setCurrentTab) props.setCurrentTab(1);
                                        }
                                    }}
                                    value={row}
                                />
                            </Grid>
                        </Grid>
                    )
                },
                functionString: 'return row?.name || ""'
            },
            {
                key: 'status',
                title: messages['dynamicFormsMain_status'],
                show: true,
                export: true,
                render: (row) => {
                    let status = { id: 0, label: messages['dynamicFormsMain_inactive'], color: "default" };
                    if (row?.formStatus === "active") {
                        status = { id: 1, label: messages['dynamicFormsMain_active'], color: "green" };
                    }

                    return (
                        <Pill
                            item={status}
                            variant={"filled"}
                            clickable={false}
                            size='dynamic'
                        />
                    )
                },
                functionString: `return row?.formStatus === "inactive" ? "${messages['dynamicFormsMain_inactive']}" : "${messages['dynamicFormsMain_active']}"`
            },
            {
                key: 'category',
                title: messages['dynamicFormsMain_category'],
                show: true,
                export: true,
                render: (row) => {
                    return (<span>{messages[`dynamicFormsMain_${row?.category}`]}</span>)
                },
                functionString: `return row?.category`
            },
            {
                key: 'tagIds',
                title: messages['dynamicFormsMain_tags'],
                show: true,
                export: true,
                render: (row) => {
                    let tagIds = row?.tagIds || [];
                    let currentTags = [];

                    tags?.forEach(tag => {
                        if (tagIds?.includes(`${tag?.id}`)) {
                            currentTags.push(tag);
                        }
                    });

                    return (
                        <div style={{ width: 200, maxWidth: 200 }}>
                            <TagsGroup
                                options={currentTags}
                                nameField="label"
                                colorField="color"
                            />
                        </div>
                    )
                },
            },
            {
                key: 'updatedOn',
                title: messages['dynamicFormsMain_updatedAt'],
                show: true,
                export: true,
                render: (row) => {
                    return (
                        <Timezone
                            date={row?.updatedOn}
                            dateFormat={dateformat}
                            timeFormat="hh:mm a"
                            timezone={timeZone}
                        />
                    )
                },
                functionString: `return row?.updatedOn ? moment(row?.updatedOn).format('${format}, hh:mm a') :  ""`
            },
            {
                key: 'createdOn',
                title: messages['dynamicFormsMain_createdAt'],
                show: true,
                export: true,
                render: (row) => {
                    return (
                        <Timezone
                            date={row?.createdOn}
                            dateFormat={dateformat}
                            timeFormat="hh:mm a"
                            timezone={timeZone}
                        />
                    )
                },
                functionString: `return row?.createdOn ? moment(row?.createdOn).format('${format}, hh:mm a') :  ""`
            }
        ]);
    }, [locale, tags]);

    useEffect(() => {
        if (error) {
            NotificationManager.error(
                messages['errorMessage'],
                messages['error'],
                3000,
                null,
                null,
                'error',
            );
            dispatch(reset({ error: null }));
        }
    }, [error]);

    useEffect(() => {
        if (exportResult && exportName === "dynamicForms") {
            NotificationManager.info(
                messages['exportInProcessBody'],
                messages['exportInProcessTitle'],
                4000,
                null,
                null,
                'info',
                null
            );
            dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
        }
    }, [exportResult, exportName]);

    const onChangePage = useCallback((newPage, newRowsPerPage, newSearchText) => {
        const newOffset = (newPage - 1) * newRowsPerPage;
        dispatch(searchDynamicForms({
            ...filters,
            openSearch: newSearchText || "",
            limit: newRowsPerPage,
            offset: newOffset
        }));
    });

    const onSearchText = useCallback((newSearchText) => {
        setSearchText(newSearchText);

        if (window.typingTime) {
            clearTimeout(window.typingTime);
        }

        window.typingTime = setTimeout(() => {
            tableRef?.current?.setPage(1);
            tableRef?.current?.setSelectedItems([]);
            setSelectedItems([]);
            onChangePage(1, rowsPerPage, newSearchText);
            window.typingTime = null;
        }, 500);
    });

    const resetTable = () => {
        setSelectedItems([]);
        tableRef?.current?.setPage(1);
        tableRef?.current?.setSelectedItems([]);
    }

    return (
        <div>
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Grid item xs={6} sm={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item>
                            <IconButton
                                tooltip={messages["filters"]}
                                placement="bottom"
                                variant="secondary"
                                icon="filter_list"
                                size="small"
                                disabled={loading}
                                onClick={() => {
                                    if (!openAdvancedFiltersModal) {
                                        dispatch(openAdvancedFilters())
                                    } else {
                                        dispatch(closeAdvancedFilters())
                                    }
                                }}
                            />
                        </Grid>

                        {
                            HasPermission(user, "dformdesign") &&
                            <>
                                <Grid item>
                                    <IconButton
                                        icon="add_box"
                                        placement="bottom"
                                        variant="secondary"
                                        size="small"
                                        tooltip={messages['add']}
                                        disabled={loading}
                                        onClick={() => {
                                            dispatch(openDesignModal());
                                        }}
                                    />
                                </Grid>

                                {
                                    (selectedItems.length > 0) &&
                                    <Grid item>
                                        <IconButton
                                            icon="delete"
                                            placement="bottom"
                                            variant="secondary"
                                            size="small"
                                            tooltip={messages['delete']}
                                            disabled={loading}
                                            onClick={() => {
                                                dispatch(openDeleteModal(selectedItems));
                                            }}
                                        />
                                    </Grid>
                                }
                            </>

                        }

                        <Grid item>
                            {/*
                            <TableButtons
                                messages={messages}
                                tableRef={tableRef}
                                //buttons={['export']}
                                buttons={[]}
                                onExport={(type) => {
                                    if (selectedItems?.length) {
                                        if (type === "Csv") {
                                            tableRef.current.exportCSV();
                                        } else if (type === "Pdf") {
                                            tableRef.current.exportPDF();
                                        }
                                    } else {
                                        const columns = tableRef.current?.columns
                                        const headers = columns.filter(x => x.show && x.export).map(x => { return { header: x.title, column: x.key, functionString: x.functionString } }) || [];
                                        const { limit, offset, openSearch, status, sortBy, dateRange } = filters;
                                        let conditions = [];
                                        let sort = [];
                                        let newStatus = "";
                                        if (status === 1) {
                                            newStatus = "active";
                                        } else if (status === 0) {
                                            newStatus = "draft";
                                        }

                                        if (newStatus) {
                                            conditions.push({
                                                fields: ["formStatus"],
                                                terms: [newStatus],
                                                exact: true
                                            });
                                        }

                                        let condition = {};
                                        if (openSearch) {
                                            condition = {
                                                fields: ["name", "description"],
                                                terms: [openSearch],
                                                exact: false
                                            }
                                        }

                                        if (dateRange?.start && dateRange?.end) {
                                            condition['range'] = {
                                                fields: ["createdOn"],
                                                GTE: dateRange?.start,
                                                LTE: dateRange?.end
                                            }
                                        }

                                        if (condition?.fields || condition?.range) {
                                            conditions.push(condition);
                                        }

                                        dispatch(sendGenericExport(
                                            "dynamicForms",
                                            type,
                                            `
                                            query searchDynamicForms ($conditions: [DynamicFormSearchInput], $sort: [DynamicFormSortInput], $limit: Int, $offset: Int) {
                                                dynamicForms: searchDynamicForms (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
                                                    items {
                                                        id
                                                        companyId
                                                        name
                                                        pages {
                                                            id
                                                            title
                                                            fields {
                                                                id 
                                                                type
                                                                label
                                                                readOnly
                                                                required
                                                                placeHolder
                                                                help
                                                            }
                                                        }
                                                        properties {
                                                            allowDraft
                                                        }
                                                        description
                                                        updatedOn
                                                        updatedBy
                                                        createdBy
                                                        createdOn
                                                    }
                                                    total
                                                }
                                            }
                                            `,
                                            {
                                                conditions,
                                                sort,
                                                limit,
                                                offset
                                            },
                                            headers,
                                            { unit: 'pt', size: 'A4', orientation: 'portrait', fontSize: 10 }
                                        ));
                                    }
                                }}
                            />
                            */}
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages['dynamicFormsMain_search']}
                        searchText={searchText}
                        onSearchText={onSearchText}
                    />
                </Grid>
            </Grid>

            <TableView
                ref={tableRef}
                keyColumn="id"
                data={data?.items || []}
                total={data?.total || 0}
                columns={columns || []}
                onChangePage={(newPage) => {
                    onChangePage(newPage, rowsPerPage, searchText);
                }}
                onChangeSort={(newSort) => {

                }}
                onChangeRowsPerPage={(newRowsPerPage) => {
                    setRowsPerPage(newRowsPerPage);
                    onChangePage(1, newRowsPerPage, searchText);
                }}
                onSelectedItems={(newSelectedItems) => {
                    setSelectedItems(newSelectedItems || []);
                }}
                loading={loading || loadingTags}
                rowsPerPage={rowsPerPage}
                hideAllInPagination={true}
                infiniteScroll={false}
                topheight={236}
                hideDragable
                searchText={searchText}
            />

            <AdvancedFilters refresh={resetTable} />

            {
                openDesignFormModal &&
                <DesignForm
                    refresh={() => {
                        resetTable();
                        onChangePage(1, rowsPerPage, searchText);
                        dispatch(getDynamicForms({}));
                    }}
                />
            }

            {
                openDeleteFormsModal &&
                <DeleteForms
                    refresh={() => {
                        resetTable();

                        if (selectedItems?.length === data?.total) {
                            setSearchText("");
                            onChangePage(1, rowsPerPage, "");
                        } else {
                            if (data?.items?.length > 1) {
                                onChangePage(1, rowsPerPage, searchText);
                            } else {
                                setSearchText("");
                                onChangePage(1, rowsPerPage, "");
                            }
                        }

                        dispatch(getDynamicForms({}));
                    }}
                />
            }

            {
                openDuplicateFormModal &&
                <DuplicateForm
                    refresh={() => {
                        resetTable();
                        onChangePage(1, rowsPerPage, searchText);
                        dispatch(getDynamicForms({}));
                    }}
                />
            }

            {
                openRenameFormModal &&
                <RenameForm
                    refresh={() => {
                        resetTable();
                        onChangePage(1, rowsPerPage, searchText);
                        dispatch(getDynamicForms({}));
                    }}
                />
            }
        </div>
    );
}

export default DynamicFormsList;